import { EDNVal, toEDNString, toEDNStringFromSimpleObject } from "edn-data";
import { queryOmegaSingleResult } from "../api";
import {
  GetPagesResult,
  Notebook,
  NotebookPage,
  PagePublished,
} from "../types/note";
import { NoteBlock } from "../types/notebook/note/block";
import { PageBlock } from "../types/notebook/note/block/page";

export const setPageName: (config: {
  appId: string;
  pageId: string;
  folderId: string;
  name: string;
}) => Promise<any> = async ({ appId, pageId, folderId, name }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["page-id", pageId],
      ["folder-id", folderId],
      ["name", name],
    ],
  });
  const query = `
    (datastore Qo.Public.Api.Page "omega/query-omega/public/api/page")
    (Qo.Public.Api.Page/set-page-name ${ednString} Response)
    (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getPages: (config: {
  "app-id": string;
  "folder-id"?: string;
  "no-page-data"?: boolean;
  skip?: number;
  limit?: number;
}) => Promise<GetPagesResult> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config, { keysAs: "string" });
  const query = `
      (datastore Qo.Public.Api.Page "omega/query-omega/public/api/page")
      (Qo.Public.Api.Page/get-pages ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getPage: (config: {
  pageId: string;
}) => Promise<NotebookPage> = async ({ pageId }) => {
  const ednString = toEDNStringFromSimpleObject(
    {
      "page-id": pageId,
    },
    { keysAs: "string" },
  );
  const query = `
      (datastore Qo.Public.Api.Page "omega/query-omega/public/api/page")
      (Qo.Public.Api.Page/get-page ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getPageBlocks: (config: {
  "page-id": string;
}) => Promise<PageBlock[]> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config, { keysAs: "string" });
  const query = `
      (datastore Qo.Public.Api.Page "omega/query-omega/public/api/page")
      (Qo.Public.Api.Page/get-page-blocks ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const addPage: (config: {
  appId: string;
  folderId?: string;
  name: string;
  isDatabase?: boolean;
}) => Promise<NotebookPage> = async ({
  appId,
  name,
  folderId,
  isDatabase = false,
}) => {
  const baseEntries: [EDNVal, EDNVal][] = [
    ["app-id", appId],
    ["name", name],
  ];
  const folderEntries: [EDNVal, EDNVal][] = folderId
    ? [["folder-id", folderId]]
    : [];
  const isDatabaseEntries: [EDNVal, EDNVal][] = isDatabase
    ? [["is-database", "true"]]
    : [];
  const entries = [...baseEntries, ...folderEntries, ...isDatabaseEntries];
  const ednString = toEDNString({
    map: entries,
  });
  const query = `
        (datastore Qo.Public.Api.Page "omega/query-omega/public/api/page")
        (Qo.Public.Api.Page/add-page ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const moveBlockIndex: (config: {
  "block-id": string;
  "app-id": string;
  "page-id": string;
  "block-index": number;
}) => Promise<any> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config, { keysAs: "string" });
  const query = `
        (datastore Qo.Public.Api.Page "omega/query-omega/public/api/page")
        (Qo.Public.Api.Page/move-block-index ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getPageIsPublished: (config: {
  "app-id": string;
  "page-id": string;
}) => Promise<{
  "page-id": string;
  "app-id": string;
  published: PagePublished;
}> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config, { keysAs: "string" });
  const query = `
        (datastore Qo.Public.Api.Page "omega/query-omega/public/api/page")
        (Qo.Public.Api.Page/published ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const setPageIsPublished: (config: {
  "app-id": string;
  "page-id": string;
  published: PagePublished;
}) => Promise<{
  "page-id": string;
  "app-id": string;
  published: PagePublished;
}> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config, { keysAs: "string" });
  const query = `
        (datastore Qo.Public.Api.Page "omega/query-omega/public/api/page")
        (Qo.Public.Api.Page/set-published ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const insertPageBlock: (config: {
  "block-index": number;
  block: NoteBlock;
}) => Promise<{
  "block-index": number;
  block: NoteBlock;
}> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config as any, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api.Page "omega/query-omega/public/api/page")
        (Qo.Public.Api.Page/insert-page-block ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const pageSearch: (config: {
  "app-id": string;
  "search-string": string;
  limit: number;
}) => Promise<NotebookPage[]> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config as any, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api.Page "omega/query-omega/public/api/page")
        (Qo.Public.Api.Page/page-search ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const movePageBlock: (config: {
  "block-id": string;
  "app-id": string;
  "page-id": string;
}) => Promise<void> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config as any, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api.Page "omega/query-omega/public/api/page")
        (Qo.Public.Api.Page/move-page-block ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const movePage: (config: {
  "source-page-id": string;
  "app-id": string;
  "destination-page-id": string;
}) => Promise<void> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config as any, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api.Page "omega/query-omega/public/api/page")
        (Qo.Public.Api.Page/move-page ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};
